import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Modal, ModalBody } from "reactstrap";

import deleteWhiteIcon from "../../../assets/img/icons/delete_white.svg";
import Plus from "../../../assets/img/icons/plus_primary.svg";
import removeAssignee from "../../../assets/img/icons/remove_assignee_black.svg";
import { RemoveButton } from "../../../components/AddMultipleMemberDropDownNew/AddMultipleMemberDropDownNew";
import CreateProjectDropdown from "../../../components/DropdownStyle/CreateProjectDropdown";
import ButtonTextLoader from "../../../components/Loaders/ButtonTextLoader";
import Select from "../../../components/ReactSelectDropdown";
import {
  BorderButtonWithIcon,
  ButtonIcon,
  ColoredButton,
  WhiteButton,
} from "../../../styledComponents/buttons";
import {
  AddClientContainerNew,
  GeneralInfoItem,
  GeneralInfoSection2,
  HoveredSectionClient,
} from "../../../styledComponents/clients";
import {
  CardTitle,
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  Container,
  ContentHeader,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  PageTitle,
} from "../../../styledComponents/common";
import { ModalButtonSection } from "../../../styledComponents/members";
import { RedStar } from "../../../styledComponents/members";
import { checkHtml, checkUrl, validateEmail } from "../../../utils/helper";
import AddLocationModal from "../../ClientsNew/ClientDetails/AddLocationModal";

const ClientModal = ({
  isOpen,
  toggle,
  selectedOrganization,
  createClient,
  isClientLoading,
  createdClientId,
  createdClientInfo,
  setSelectedClient,
  // Location related props
  addClientLocation,
  getClientLocation,
  addOrUpdateclientLocalMapLoading,
  createdClientLocationInfo,
  clientLocationList: externalClientLocationList,
  clientId,
  orgType,
}) => {
  const { t } = useTranslation();
  const DEBUG_MAP_CENTER =
    process.env.NODE_ENV !== "production" &&
    (process.env.REACT_APP_DEBUG_MAP_CENTER === "true" ||
      process.env.REACT_APP_DEBUG_MAP_CENTER === "1");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [saveErrors, setSaveErrors] = useState({});
  const customerInputRef = useRef(null);
  const [clientLocationList, setClientLocationList] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  // Store location form data for AddLocationModal callback
  const [pendingLocationData, setPendingLocationData] = useState(null);

  useEffect(() => {
    if (isOpen) {
      customerInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (createdClientId !== null && isOpen) {
      if (createdClientInfo && createdClientInfo.id) {
        setSelectedClient({
          value: createdClientInfo.id,
          label: createdClientInfo.name,
        });
        // Close modal after client is created
        setClientName("");
        setClientEmail("");
        setPhoneNumber("");
        setClientLocationList([]);
        toggle();
      }
    }
  }, [createdClientId, createdClientInfo]);

  // Reset location list when modal opens (for service type)
  useEffect(() => {
    if (isOpen && orgType === "service") {
      setClientLocationList([]);
    }
  }, [isOpen, orgType]);

  // Sync external clientLocationList if provided (for non-service types only)
  useEffect(() => {
    if (externalClientLocationList && orgType !== "service") {
      setClientLocationList(externalClientLocationList);
    }
  }, [externalClientLocationList, orgType]);

  const addLocationToggle = () => {
    setPopupOpen(!popupOpen);
  };

  useEffect(() => {
    if (!DEBUG_MAP_CENTER) return;
    if (!popupOpen) return;
    console.debug("[ClientModal] opening AddLocationModal", {
      orgType,
      selectedOrganizationId: selectedOrganization?.id,
      createdClientId,
      createdClientInfoId: createdClientInfo?.id,
      clientId,
      clientLocationListCount: clientLocationList?.length || 0,
    });
  }, [
    DEBUG_MAP_CENTER,
    popupOpen,
    orgType,
    selectedOrganization?.id,
    createdClientId,
    createdClientInfo?.id,
    clientId,
    clientLocationList,
  ]);

  const onHoverChange = id => {
    setHoveredId(id);
  };

  const handleClientLocationDelete = index => {
    const updatedList = clientLocationList.filter((_, i) => i !== index);
    setClientLocationList(updatedList);
  };

  // Handle location addition from AddLocationModal (for service type - store locally, no API call)
  // This receives the payload object from AddLocationModal
  // When action="create", AddLocationModal sends payload with: name, address, radius, default, latitude, longitude
  // Note: AddLocationModal uses organizationID (uppercase) but deletes organizationId (lowercase) when action="create"
  const handleClientLocation = payload => {
    if (!selectedOrganization || !selectedOrganization.id) return;

    // Extract data from AddLocationModal payload
    // Payload from AddLocationModal: { organizationID, name, address, radius, default, latitude, longitude }
    // When action="create", organizationId (lowercase) is deleted but organizationID (uppercase) may still exist
    const newLocation = {
      organizationId: selectedOrganization.id, // Always use from selectedOrganization
      name: payload.name || "",
      address: payload.address || "",
      radius: payload.radius || 300,
      default: payload.default || false,
      latitude: payload.latitude,
      longitude: payload.longitude,
    };

    // If new location is default, remove default from all existing locations
    const updatedList = clientLocationList.map(loc => ({
      ...loc,
      default: newLocation.default ? false : loc.default,
    }));

    setClientLocationList([...updatedList, newLocation]);
    setPopupOpen(false);
  };

  const onClientNameChange = e => {
    const { value } = e.target;
    setClientName(value);
    if (saveErrors && saveErrors["clientName"]) {
      delete saveErrors["clientName"];
    }
  };

  const onEmailChange = e => {
    const { value } = e.target;
    setClientEmail(value);
    if (saveErrors && saveErrors["clientEmail"]) {
      delete saveErrors["clientEmail"];
    }
  };

  const handleSave = () => {
    if (checkError()) {
      if (selectedOrganization && selectedOrganization.id) {
        let payload = {
          organization_id: selectedOrganization.id,
          name: clientName,
          phone_number: phoneNumber
            ? phoneNumber.startsWith("+")
              ? phoneNumber
              : "+" + phoneNumber
            : null,
          email: clientEmail ? clientEmail : null,
        };

        // For service type: include location_list in payload if locations exist
        if (
          orgType === "service" &&
          clientLocationList &&
          clientLocationList.length > 0
        ) {
          // Format locations according to the required structure
          payload.location_list = clientLocationList.map(loc => ({
            organizationId: loc.organizationId || selectedOrganization.id,
            name: loc.name,
            address: loc.address,
            radius: loc.radius || 300,
            default: loc.default || false,
            latitude: loc.latitude,
            longitude: loc.longitude,
          }));
        }

        createClient(payload);
      }
    }
  };

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;
    if (!clientName.trim()) {
      formIsValid = false;
      saveErrors["clientName"] = t("please_enter_customer_name");
    } else if (clientName.length > 256) {
      formIsValid = false;
      saveErrors["clientName"] = t(
        "please_enter_customer_name_within_256_characters",
      );
    } else if (checkUrl(clientName)) {
      formIsValid = false;
      saveErrors["clientName"] = t("client_name_can_not_contain_url");
    } else if (checkHtml(clientName)) {
      formIsValid = false;
      saveErrors["clientName"] = t("invalid_client_name");
    }
    if (!validateEmail(clientEmail)) {
      formIsValid = false;
      saveErrors["clientEmail"] = t("please_enter_a_valid_email_address");
    }
    setSaveErrors(saveErrors);
    return formIsValid;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      style={{ marginTop: `20vh`, maxWidth: "500px" }}
    >
      <ModalBody style={{ padding: `30px` }}>
        <CommonFlex justifyContent="space-between" alignItems="center">
          <PageTitle
            fontSize="16px"
            style={{ paddingLeft: `10px`, paddingBottom: `15px` }}
          >
            {t("add_customer_details")}
          </PageTitle>
          <RemoveButton onClick={toggle} title={t("close")}>
            <CommonImage src={removeAssignee} alt="close" size="8px" />
          </RemoveButton>
        </CommonFlex>
        <>
          <AddClientContainerNew
            style={{
              paddingLeft: `10px`,
              paddingBottom: `15px`,
              width: "100%",
            }}
          >
            <InputWithLabelSection>
              <InputLabel>
                {t("customer_name")} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t("type_customer_name_here")}
                ref={customerInputRef}
                value={clientName}
                onChange={e => onClientNameChange(e)}
                error={
                  saveErrors &&
                  saveErrors["clientName"] &&
                  saveErrors["clientName"] !== undefined
                }
              />
              {saveErrors &&
                saveErrors["clientName"] &&
                saveErrors["clientName"] !== undefined && (
                  <FormAlert margin="0 0 0 5px">
                    {saveErrors["clientName"]}
                  </FormAlert>
                )}
            </InputWithLabelSection>
            <InputWithLabelSection>
              <InputLabel>
                {t("email_address")} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t("type_email_address_here")}
                value={clientEmail}
                onChange={e => onEmailChange(e)}
                error={
                  saveErrors &&
                  saveErrors["clientEmail"] &&
                  saveErrors["clientEmail"] !== undefined
                }
              />
              {saveErrors &&
                saveErrors["clientEmail"] &&
                saveErrors["clientEmail"] !== undefined && (
                  <FormAlert margin="0 0 0 5px">
                    {saveErrors["clientEmail"]}
                  </FormAlert>
                )}
            </InputWithLabelSection>
            <InputWithLabelSection>
              <InputLabel>{t("contact_number")}</InputLabel>
              <PhoneInput
                enableSearch
                country={"us"}
                value={phoneNumber}
                onChange={phone => setPhoneNumber(phone)}
                inputClass="phone-number-input"
                buttonClass="phone-number-flag-dropdown"
              />
            </InputWithLabelSection>
            <InputWithLabelSection>
              <InputLabel>{t("location")}</InputLabel>
              <CommonFlex justifyContent="flex-start">
                <BorderButtonWithIcon
                  borderRadius="30px"
                  margin="0"
                  borderColor="#2266EF"
                  onClick={() => {
                    // For service type: allow adding locations before client is created
                    // Locations will be stored locally and sent with client creation
                    if (orgType === "service") {
                      addLocationToggle();
                    } else {
                      // For other types: only allow adding locations after client is created
                      if (createdClientInfo?.id || clientId) {
                        addLocationToggle();
                      } else {
                        console.log(
                          "Please save the customer first to add locations",
                        );
                      }
                    }
                  }}
                >
                  <ButtonIcon src={Plus} alt="plus" />
                  {clientLocationList?.length >= 1
                    ? t("add_more_location")
                    : t("add_location")}
                </BorderButtonWithIcon>
              </CommonFlex>
            </InputWithLabelSection>

            {clientLocationList && clientLocationList.length > 0 && (
              <GeneralInfoSection2
                style={{ marginTop: "10px", minWidth: "200px" }}
                columns="1fr 1fr"
              >
                {clientLocationList.map((item, index) => (
                  <div
                    key={index}
                    onMouseEnter={() => onHoverChange(index)}
                    onMouseLeave={() => onHoverChange(null)}
                    style={{
                      position: "relative",
                      zIndex: hoveredId === index ? 1 : "auto",
                      width: "100%",
                      
                    }}
                  >
                    <CommonText $label>
                      {t("service_location")} {index + 1}{" "}
                      {item.default ? `(${t("default")})` : ""}
                    </CommonText>

                    <div
                      style={{
                        paddingBottom: "10px",
                        paddingTop: "10px",
                        cursor: "pointer",
                        width: "200px",
                        position: "relative",
                      }}
                    >
                      {hoveredId !== null && hoveredId === index ? (
                        <HoveredSectionClient>
                          <CommonImage
                            src={deleteWhiteIcon}
                            alt=""
                            size="16px"
                            onClick={() => handleClientLocationDelete(index)}
                          />
                        </HoveredSectionClient>
                      ) : null}
                      <GeneralInfoItem
                        minHeight="130px"
                        backColor={(index + 3) % 8}
                      >
                        <CommonText title>{item.address}</CommonText>
                        <CommonText $label fontWeight="500">
                          {item.name}
                        </CommonText>
                      </GeneralInfoItem>
                    </div>
                  </div>
                ))}
              </GeneralInfoSection2>
            )}
          </AddClientContainerNew>
        </>

        {selectedOrganization && selectedOrganization.id && (
          <AddLocationModal
            isOpen={popupOpen}
            toggle={addLocationToggle}
            addClientLocation={
              orgType === "service" ? handleClientLocation : addClientLocation
            }
            organization_id={selectedOrganization.id}
            selectedOrganization={selectedOrganization}
            client_id={
              orgType === "service"
                ? null
                : createdClientInfo?.id || clientId || null
            }
            action={orgType === "service" ? "create" : "add"}
            addOrUpdateclientLocalMapLoading={
              orgType === "service" ? false : addOrUpdateclientLocalMapLoading
            }
            createdClientLocationInfo={
              orgType === "service" ? null : createdClientLocationInfo
            }
            getClientLocation={orgType === "service" ? null : getClientLocation}
          />
        )}

        <ModalButtonSection
          style={{ justifyContent: "start", paddingLeft: `10px` }}
        >
          <WhiteButton type="cancel" onClick={toggle}>
            Cancel
          </WhiteButton>
          <ColoredButton
            padding={isClientLoading && "0 24px"}
            onClick={!isClientLoading ? () => handleSave() : ""}
            // disabled={
            //   hourlyRatesType.value === 'work_order_hourly' &&
            //   !workOrderHourlyRate
            // }
          >
            {isClientLoading ? (
              <ButtonTextLoader loadingText="Saving" fontSize="13px" />
            ) : (
              t("add_customer")
            )}
          </ColoredButton>
        </ModalButtonSection>
      </ModalBody>
    </Modal>
  );
};

export default ClientModal;
